import type React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  Box,
  Card,
  Heading,
  Icon,
  Image,
  Menu,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconCreditScoreFilled } from '@zillow/constellation-icons';
import ArrayCreditOverviewMini from './array/ArrayCreditOverviewMini';
import faqs from '../../../constants/FaqConstants';
import creditOverviewSvg from '../../../../../../assets/creditOverview.svg';
import ArrayAccountEnroll from './array/ArrayAccountEnroll';
import { CREDIT_REPORTING_FAQ_URL } from '../../../constants/rentReportingConstants';
import { CreditTrackingOptOutModal } from './CreditTrackingOptOutModal';
import type { RootState } from '../../../../../store/types';
import type { CreditTrackingInterface } from '../../../models/Types';
import { useUnenrollRenterFromCreditTracking } from '../../../hooks/usePaymentReporting';
import {
  CREDIT_TRACKING_UNENROLL_FAILURE,
  CREDIT_TRACKING_UNENROLL_SUCCESS,
} from '@renter-hub/constants/notifications';
import { showPageNotification } from '@common/actions/NotificationActions';
import { updateCreditTrackingEnrollmentStatus } from '@renter-hub/actions/onboarding';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';

const CreditTrackingCard: React.FC<{ creditTracking?: CreditTrackingInterface }> = ({ creditTracking }) => {
  const dispatch = useDispatch();

  const { arrayUserToken = null } = creditTracking ?? ({} as CreditTrackingInterface);

  const {
    firstName = '',
    lastName = '',
    email = '',
  } = useSelector((state: RootState) => state.payments.renter.dashboard.renterProfile);

  const {
    homeAddress: streetAddress = '',
    city = '',
    state = '',
    zipCode = '',
  } = useSelector((state: RootState) => state.payments.renter.dashboard.rentTerms.addressInfo);

  const creditTrackingFaqs = faqs.PAYMENT_SETTINGS_V2.CREDIT_TRACKING.body();
  const [trackingOptOutModalOpen, setTrackingOptOutModalOpen] = useState(false);

  // React Query hook for unenrolling from credit tracking
  const unenrollMutation = useUnenrollRenterFromCreditTracking();

  const handleUnenrollRenterFromCreditTracking = async () => {
    try {
      RachelCSEvents.emitOptOutCreditTrackingEvent();
      await unenrollMutation.mutateAsync();
      dispatch(showPageNotification(CREDIT_TRACKING_UNENROLL_SUCCESS));
      await dispatch(updateCreditTrackingEnrollmentStatus('OPTED_OUT'));
      setTrackingOptOutModalOpen(false);
    } catch {
      dispatch(showPageNotification(CREDIT_TRACKING_UNENROLL_FAILURE));
      setTrackingOptOutModalOpen(false);
    }
  };

  const handleExpandedChange = (value: string | Array<string>) => {
    if (value) {
      RachelCSEvents.emitFaqCreditTrackingEvent(value as string);
    }
  };

  const unenrolledContent = (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'tighter',
        padding: 'default',
      }}
    >
      <Box
        css={{
          display: 'flex',
          justifyContent: 'flex-start',
          '@media (min-width: 768px)': {
            justifyContent: 'center',
          },
        }}
      >
        <Image
          alt="credit-overview"
          css={{ height: '108px', marginBottom: '4px' }}
          src={creditOverviewSvg as unknown as string}
        />
      </Box>
      <Box
        css={{
          textAlign: 'left',
          maxWidth: '570px',
          margin: '0',
          '@media (min-width: 768px)': {
            textAlign: 'center',
            margin: '0 auto',
          },
        }}
      >
        <Paragraph textStyle="body-bold">Make the most of rent reporting</Paragraph>
        <Paragraph>
          Track your credit score directly on your payments dashboard while we report your payments. Zillow won’t store
          any of your credit data.
        </Paragraph>
      </Box>
      <ArrayAccountEnroll
        firstName={firstName}
        lastName={lastName}
        streetAddress={streetAddress}
        city={city}
        state={state}
        zipCode={zipCode}
        email={email}
      />
    </Box>
  );

  const enrolledContent = (
    <>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
          },
          gap: 'tighter',
          padding: '0 token(spacing.sm) token(spacing.sm) token(spacing.sm)',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Box css={{ flexBasis: '50%' }}>
          <ArrayCreditOverviewMini userToken={arrayUserToken} />
        </Box>
        <Box css={{ flexBasis: '50%' }}>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'tighter',
            }}
          >
            <Heading level={6}>Frequently asked questions</Heading>
            <TextButton asChild tone="neutral" textStyle="body" css={{ textDecoration: 'underline' }}>
              <a href={CREDIT_REPORTING_FAQ_URL} target="_blank" rel="noreferrer">
                View all
              </a>
            </TextButton>
          </Box>
          <Accordion.Root type="single" onExpandedChange={handleExpandedChange}>
            {creditTrackingFaqs.map((faq, index) => (
              <Accordion.Item value={faq.key ?? `accordion-${index}`} key={faq.key ?? `accordion-${index}`}>
                <Accordion.Header css={{ padding: '12px 16px' }}>
                  <Text textStyle="body-bold">{faq.question}</Text>
                </Accordion.Header>
                <Accordion.Panel css={{ padding: '12px 16px' }}>
                  <Paragraph>{faq.answer}</Paragraph>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <Card
        elevated={false}
        header={
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '100%',
              padding: 'token(spacing.xs) token(spacing.xs) token(spacing.xs) token(spacing.sm)',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Icon render={<IconCreditScoreFilled />} css={{ marginRight: 'tighter' }} />
              <Heading level={6}>Credit tracking</Heading>
            </Box>
            {arrayUserToken && (
              <Menu
                placement="bottom-end"
                content={
                  <Box css={{ zIndex: '1000' }}>
                    <Menu.Item>
                      <Menu.ItemLabel onClick={() => setTrackingOptOutModalOpen(true)}>
                        Opt out of credit tracking
                      </Menu.ItemLabel>
                    </Menu.Item>
                  </Box>
                }
              />
            )}
          </Box>
        }
      >
        {arrayUserToken ? enrolledContent : unenrolledContent}
      </Card>
      <CreditTrackingOptOutModal
        open={trackingOptOutModalOpen}
        onClose={() => setTrackingOptOutModalOpen(false)}
        onOptOut={handleUnenrollRenterFromCreditTracking}
      />
    </>
  );
};

export default CreditTrackingCard;
